import React, { useState } from "react"
import PropTypes from "prop-types"
import ReactJoyride from "react-joyride"
import {
  Row,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Table,
  CardSubtitle,
  Spinner,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
  Collapse,
} from "reactstrap"
import { useStateValue } from "../../contexts/StateProvider"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons"
import { Button } from "../Reactstrap_Components/Button"
import "./demoapi.css"
import patentSample from "./patent_sample.json"
import patentKeywordSample from "./patent_keyword_sample.json"
import wikiSample from "./wikipedia_sample.json"
import arxivSample from "./arxiv_sample.json"
import UnpaidModal from "../UnpaidModal"
import TrialAlert from "../TrialAlert"
import PastDueAlert from "../PastDueAlert"
import TraindexApi from "../../api/TraindexAPI"
import Select from "react-select"

const defaultPatentFilters = {
  priority_date: "",
  priority_date_criteria: ">=",
  publication_date: "",
  publication_date_criteria: ">=",
  kind_code: "",
  cpc: "",
}

const dateCriteriaOptions = [
  { label: "Greater than", value: ">" },
  { label: "Greater or equal", value: ">=" },
  { label: "Less than", value: "<" },
  { label: "Less or equal", value: "<=" },
  { label: "Equals", value: "=" },
]

const toApiDate = (dateStr) => {
  if (!dateStr) return null
  const normalized = String(dateStr).replace(/\D/g, "")
  return normalized.length === 8 ? parseInt(normalized, 10) : null
}

const buildPatentRequest = (searchText, filters) => {
  const request = { conceptFeaturesString: searchText.trim(), maxNumItems: 100 }
  const priorityDate = toApiDate(filters.priority_date)
  if (priorityDate) {
    request.priority_date = priorityDate
    request.priority_date_criteria = filters.priority_date_criteria
  }
  const publicationDate = toApiDate(filters.publication_date)
  if (publicationDate) {
    request.publication_date = publicationDate
    request.publication_date_criteria = filters.publication_date_criteria
  }
  if (filters.kind_code) {
    request.kind_code = filters.kind_code
  }
  if (filters.cpc.trim()) {
    request.cpc = filters.cpc.trim().toUpperCase()
  }
  return request
}

const getQueryPlaceholder = (activeTab, searchMode) => {
  if (activeTab === "1") {
    return searchMode === "keyword"
      ? "Boolean query, e.g. battery AND lithium, TI:(wireless charging), or CPC:H01M*"
      : "Natural-language description, e.g. a drone delivery system with obstacle avoidance and automated landing"
  }
  if (activeTab === "2") {
    return "Wikipedia topic or passage, e.g. supervised learning in computer vision"
  }
  return "Research topic or abstract text, e.g. attention mechanisms for neural machine translation"
}

const DemoAPI = () => {
  const { state, dispatch } = useStateValue(),
    { user } = state,
    [patents, setPatents] = useState(""),
    [articles, setArticles] = useState(""),
    [arxivSamples, setArxivSamples] = useState(""),
    [loading, setLoading] = useState(false),
    [activeTab, setActiveTab] = useState("1"),
    [error, setError] = useState(""),
    [dropDownValue, setDropDownValue] = useState("Patents"),
    [searchMode, setSearchMode] = useState("semantic"),
    [searchValue, setSearchValue] = useState(""),
    [patentFilters, setPatentFilters] = useState(defaultPatentFilters),
    [filtersOpen, setFiltersOpen] = useState(false),
    [optionValue, setOptionValue] = useState({ label: "Insert sample query", value: "" }),
    patentNumbers = Object.keys(patentSample),
    patentKeywordQueries = Object.keys(patentKeywordSample),
    wikiArticles = Object.keys(wikiSample),
    arxivTitles = Object.keys(arxivSample),
    [keyString, setKeyString] = useState("demo-search")

  const steps = [
    {
      title: "Toggle Indexes",
      target: ".search-dropdown",
      content: "You can select one of the built-in indexes here to perform the search over.",
      floaterProps: {
        disableAnimation: true,
      },
      placement: "top",
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      title: "Add search text",
      target: ".demo-input",
      content: (
        <p>
          Paste the text you want to search for, e.g.
          <p id="search-text-example">
            a mechanical switch having a plurality of separate and independent switch positions which comprise a first
            switch position ad a second switch position, the mechanical switch being configured to switch among a
            plurality of programmable user interface profile settings which comprise a first profile setting and a
            second profile setting.
          </p>
        </p>
      ),
      floaterProps: {
        disableAnimation: true,
      },
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      title: "Choose Samples",
      target: ".sample-dropdown",
      content: "You could also choose from our sample texts",
      floaterProps: {
        disableAnimation: true,
      },
      disableBeacon: true,
      spotlightClicks: true,
    },
    {
      title: "Search",
      target: ".search_btn",
      content: "Click on the search button to start a search in " + dropDownValue,
      floaterProps: {
        disableAnimation: true,
      },
      disableBeacon: true,
      spotlightClicks: true,
    },
  ]

  const selectIndex = (e) => {
    setDropDownValue(e.currentTarget.textContent)
    setSearchValue("")
    setSearchMode("semantic")
    setPatentFilters(defaultPatentFilters)
    setFiltersOpen(false)
    setPatents("")
    setArticles("")
    setArxivSamples("")
    setOptionValue({ label: "Insert sample query", value: "" })
    if (activeTab !== e.target.value) setActiveTab(e.target.value)
    error && setError("")
  }

  const selectSearchMode = (e) => {
    setSearchMode(e.target.value)
    setSearchValue("")
    setPatentFilters(defaultPatentFilters)
    setPatents("")
    setOptionValue({ label: "Insert sample query", value: "" })
    error && setError("")
  }

  const patentsOptions = patentNumbers.map((patentNumber) => {
    return { value: patentNumber, label: patentNumber }
  })

  const articleOptions = wikiArticles.map((article) => {
    return { value: article, label: article }
  })

  const arxivOptions = arxivTitles.map((title) => {
    return { value: title, label: title }
  })

  const keywordOptions = patentKeywordQueries.map((queryLabel) => {
    return { value: queryLabel, label: queryLabel }
  })

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setPatentFilters((prev) => ({ ...prev, [name]: value }))
  }

  let sampleOptions = activeTab === "1"
    ? (searchMode === "keyword" ? keywordOptions : patentsOptions)
    : (activeTab === "2" ? articleOptions : arxivOptions)

  const onSubmitRequest = async (event) => {
    event.preventDefault()
    setLoading(true)
    dispatch({
      type: "SET_IS_SIGN_UP",
      isSignUp: false,
    })
    error && setError("")
    try {
      const apiMode = activeTab === "1" ? "patents" : (activeTab === "2" ? "wiki" : "arxiv")
      const payload = {
        request: apiMode === "patents"
          ? buildPatentRequest(searchValue, patentFilters)
          : { conceptFeaturesString: searchValue.trim(), maxNumItems: 100 },
        api_mode: apiMode
      }
      if (apiMode === "patents" && searchMode === "keyword") {
        payload.search_mode = "keyword"
      }
      const response = await TraindexApi.getTraindexData(payload)
      activeTab === "1" ? setPatents(response) : 
        (activeTab === "2" ? setArticles(response) : setArxivSamples(response))
      setLoading(false)
    } catch (error) {
      setError(error.message || "An error occurred. Please try again.")
      setLoading(false)
    }
  }

  const patentsDisplay =
    patents &&
    (patents.foundItems.length ? (
      patents.foundItems.map((item, index) => {
        let urlPath = item.ucid.replace(/-/g, "")
        const title = item.title && item.title.charAt(0).toUpperCase() + item.title.slice(1)
        const urlLink = `https://patents.google.com/patent/${urlPath}`
        const score = item.conceptScore != null ? item.conceptScore.toFixed(4) : "—"
        return (
          <tr key={index}>
            <th scope="row">{index + 1}</th>
            <td>{item.ucid}</td>
            <td>
              <a href={urlLink} target="_blank" rel="noopener noreferrer">
                {title || urlLink}
              </a>
            </td>
            <td>{score}</td>
          </tr>
        )
      })
    ) : (
      <tr className="text-center">
        <td colSpan="4">No item found.</td>
      </tr>
    ))

  const articlesDisplay =
    articles &&
    (articles.foundItems.length ? (
      articles.foundItems.map((item, index) => {
        return (
          <tr key={index}>
            <th scope="row">{index + 1}</th>
            <td>{item.ID}</td>
            <td>
              <a href={item.link} target="_blank" rel="noopener noreferrer">
                {item.title}
              </a>
            </td>
            <td>{item.conceptScore.toFixed(4)}</td>
          </tr>
        )
      })
    ) : (
      <tr className="text-center">
        <td colSpan="4">No item found.</td>
      </tr>
    ))

  const arxivSamplesDisplay =
    arxivSamples &&
    (arxivSamples.foundItems.length ? (
      arxivSamples.foundItems.map((item, index) => {
        let [ id_start, id_end ] = item.id.split(".")
        if (id_start.length === 3) id_start = "0" + id_start
        if (id_end.length < 4) {
          id_end = id_end + "0".repeat(4 - id_end.length)
        } else if (id_end.length > 4) {
          id_end = id_end.substring(0,4)
        }
        const fixed_id = id_start + "." + id_end
        const urlLink = `https://arxiv.org/abs/${fixed_id}`
        return (
          <tr key={index}>
            <th scope="row">{index + 1}</th>
            <td>{fixed_id}</td>
            <td>
              <a href={urlLink} target="_blank" rel="noopener noreferrer">
                {item.title || urlLink}
              </a>
            </td>
            <td>{item.conceptScore.toFixed(4)}</td>
          </tr>
        )
      })
    ) : (
      <tr className="text-center">
        <td colSpan="4">No item found.</td>
      </tr>
    ))

  const handleSelectSample = (option) => {
    setOptionValue(option)
    if (activeTab === "1") {
      if (searchMode === "keyword") {
        setSearchValue(patentKeywordSample[option.value])
        setKeyString(patentKeywordSample[option.value])
      } else {
        setSearchValue(patentSample[option.value])
        setKeyString(patentSample[option.value])
      }
    } else if (activeTab == "2") {
      setSearchValue(wikiSample[option.value])
      setKeyString(wikiSample[option.value])
    } else {
      setSearchValue(arxivSample[option.value])
      setKeyString(arxivSample[option.value])
    }
  }

  const joyRideCallback = (data) => {
    data.action === "reset" ||
      (data.action === "close" &&
        dispatch({
          type: "SET_IS_SIGN_UP",
          isSignUp: false,
        }))
  }

  return (
    <div className={`${user && user.subscription._isExpired ? "blur-bg" : "justify-content-center px-1"}`}>
      <div>
        <Container className="mx-auto mt-5">
          {user && user.subscription && <TrialAlert trial_end={user.subscription.trial_end} />}
          {user && user.subscription && <PastDueAlert subscription={user.subscription} />}
          <ReactJoyride
            callback={joyRideCallback}
            steps={steps}
            continuous={true}
            showSkipButton={true}
            scrollToFirstStep
            run={state.isSignUp}
            styles={{
              options: {
                height: 0,
                primaryColor: "#4c2e74",
              },
            }}
            locale={{
              last: "End",
            }}
          />
          <p>
            Try the Traindex search APIs from your browser. On Patents, use Semantic (v7) for natural-language
            prior art search or Keyword / Boolean (v7-keyword) for field-qualified queries. Optional filters narrow
            results by date, kind code, or CPC.
          </p>
          <p>
            For full API control, use a client such as{" "}
            <a href="https://insomnia.rest/" className="text-decoration-none" target="_blank" rel="noopener noreferrer">
              Insomnia
            </a>{" "}
            and{" "}
            <a
              href="https://www.postman.com/"
              className="text-decoration-none"
              target="_blank"
              rel="noopener noreferrer"
            >
              Postman
            </a>
          </p>
        </Container>
      </div>
      <div className="justify-content-center px-1 mt-5">
        <Container className="mt-4 mx-auto">
          <Form className="mx-auto" onSubmit={onSubmitRequest}>
            <FormGroup>
              <div className="d-flex text-center">
                <UncontrolledDropdown className="my-3 mr-3 search-dropdown">
                  <DropdownToggle className="search-dropdown-toggle py-2 px-2 bg-transparent border-0" caret>
                    {dropDownValue}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem value="1" onClick={selectIndex}>
                      Patents
                    </DropdownItem>
                    <DropdownItem value="2" onClick={selectIndex}>
                      Wikipedia
                    </DropdownItem>
                    <DropdownItem value="3" onClick={selectIndex}>
                      Arxiv
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
                {activeTab === "1" && (
                  <Button
                    type="button"
                    color="link"
                    className="my-3 mr-3 search-filters-toggle text-decoration-none"
                    onClick={() => setFiltersOpen((open) => !open)}
                  >
                    Filters {filtersOpen ? "▲" : "▼"}
                  </Button>
                )}
                <div className="mt-4">
                  <FontAwesomeIcon icon={faQuestionCircle} id="QuestionMark" />
                  <UncontrolledTooltip placement="right" target="QuestionMark">
                    Only indexes on the active status are listed
                  </UncontrolledTooltip>
                </div>
              </div>
              {activeTab === "1" && (
                <FormGroup tag="fieldset" className="search-mode-radios mb-3">
                  <FormGroup check inline>
                    <Label check>
                      <Input
                        type="radio"
                        name="searchMode"
                        value="semantic"
                        checked={searchMode === "semantic"}
                        onChange={selectSearchMode}
                      />{" "}
                      Semantic
                    </Label>
                  </FormGroup>
                  <FormGroup check inline className="ml-3">
                    <Label check>
                      <Input
                        type="radio"
                        name="searchMode"
                        value="keyword"
                        checked={searchMode === "keyword"}
                        onChange={selectSearchMode}
                      />{" "}
                      Keyword / Boolean
                    </Label>
                  </FormGroup>
                </FormGroup>
              )}
              {activeTab === "1" && (
                <Collapse isOpen={filtersOpen}>
                  <div className="search-filters-panel">
                    <Row>
                      <Col md="6" className="mb-3">
                        <label htmlFor="priority_date_criteria">Priority date</label>
                        <div className="d-flex">
                          <Input
                            type="select"
                            name="priority_date_criteria"
                            id="priority_date_criteria"
                            value={patentFilters.priority_date_criteria}
                            onChange={handleFilterChange}
                            className="mr-2"
                            style={{ maxWidth: "140px" }}
                          >
                            {dateCriteriaOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </Input>
                          <Input
                            type="date"
                            name="priority_date"
                            id="priority_date"
                            value={patentFilters.priority_date}
                            onChange={handleFilterChange}
                          />
                        </div>
                      </Col>
                      <Col md="6" className="mb-3">
                        <label htmlFor="publication_date_criteria">Publication date</label>
                        <div className="d-flex">
                          <Input
                            type="select"
                            name="publication_date_criteria"
                            id="publication_date_criteria"
                            value={patentFilters.publication_date_criteria}
                            onChange={handleFilterChange}
                            className="mr-2"
                            style={{ maxWidth: "140px" }}
                          >
                            {dateCriteriaOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </Input>
                          <Input
                            type="date"
                            name="publication_date"
                            id="publication_date"
                            value={patentFilters.publication_date}
                            onChange={handleFilterChange}
                          />
                        </div>
                      </Col>
                      <Col md="4" className="mb-2">
                        <label htmlFor="kind_code">Kind code</label>
                        <Input
                          type="select"
                          name="kind_code"
                          id="kind_code"
                          value={patentFilters.kind_code}
                          onChange={handleFilterChange}
                        >
                          <option value="">Any</option>
                          <option value="A1">A1</option>
                          <option value="A2">A2</option>
                          <option value="A9">A9</option>
                          <option value="B1">B1</option>
                          <option value="B2">B2</option>
                        </Input>
                      </Col>
                      <Col md="8" className="mb-2">
                        <label htmlFor="cpc">CPC</label>
                        <Input
                          type="text"
                          name="cpc"
                          id="cpc"
                          value={patentFilters.cpc}
                          onChange={handleFilterChange}
                          placeholder="e.g. H01M or H01M*"
                        />
                      </Col>
                    </Row>
                  </div>
                </Collapse>
              )}
              <Input
                key={keyString}
                className="demo-input"
                autoFocus
                type="textarea"
                rows="5"
                name="api"
                value={searchValue}
                placeholder={getQueryPlaceholder(activeTab, searchMode)}
                onChange={(event) => setSearchValue(event.target.value)}
                id="api"
              />
              <div className="d-flex align-items-center my-3">
                <Select
                  className="demo-select default-select text-left"
                  options={sampleOptions}
                  value={optionValue}
                  onChange={(option) => handleSelectSample(option)}
                />
                <Button className="purple_btn search_btn" type="submit" disabled={!searchValue}>
                  Search
                </Button>
              </div>
            </FormGroup>
            {error ? <CardSubtitle className="align-self-center mt-2 text-danger">{error}</CardSubtitle> : null}
          </Form>
          {loading && (
            <div className="d-flex align-items-center justify-content-center">
              <Spinner size="lg" className="spinner" />
            </div>
          )}
          {patentsDisplay && (
            <Row className="mt-5">
              <Col sm="12">
                <Table responsive className="demo_table">
                  <thead>
                    <tr style={{ backgroundColor: "#F2F2F2" }}>
                      <th style={{ width: "5%" }}>#</th>
                      <th style={{ width: "20%" }}>Patent UCID</th>
                      <th style={{ width: "55%" }}>Link</th>
                      <th style={{ width: "20%" }}>{searchMode === "keyword" ? "Relevance" : "Score"}</th>
                    </tr>
                  </thead>
                  <tbody>{patentsDisplay}</tbody>
                </Table>
              </Col>
            </Row>
          )}
          {articlesDisplay && (
            <Row className="mt-5">
              <Col sm="12">
                <Table responsive className="demo_table">
                  <thead>
                    <tr style={{ backgroundColor: "#F2F2F2" }}>
                      <th style={{ width: "5%" }}>#</th>
                      <th style={{ width: "20%" }}>Article ID</th>
                      <th style={{ width: "55%" }}>Title</th>
                      <th style={{ width: "20%" }}>Score</th>
                    </tr>
                  </thead>
                  <tbody>{articlesDisplay}</tbody>
                </Table>
              </Col>
            </Row>
          )}
          {arxivSamplesDisplay && (
            <Row className="mt-5">
              <Col sm="12">
                <Table responsive className="demo_table">
                  <thead>
                    <tr style={{ backgroundColor: "#F2F2F2" }}>
                      <th style={{ width: "5%" }}>#</th>
                      <th style={{ width: "20%" }}>Article ID</th>
                      <th style={{ width: "55%" }}>Title</th>
                      <th style={{ width: "20%" }}>Score</th>
                    </tr>
                  </thead>
                  <tbody>{arxivSamplesDisplay}</tbody>
                </Table>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      {user && user.subscription._isExpired && <UnpaidModal modal={true} />}
    </div>
  )
}

DemoAPI.propTypes = {
  history: PropTypes.object.isRequired,
}

export default DemoAPI
